import { DependencyList, Dispatch, SetStateAction, useEffect, useState } from 'react';

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  setData: Dispatch<SetStateAction<T | null>>;
}

export const useAsyncData = <T,>(loader: (signal: AbortSignal) => Promise<T>, dependencies: DependencyList = []): AsyncState<T> => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const run = async (signal: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const nextData = await loader(signal);
      if (!signal.aborted) {
        setData(nextData);
      }
    } catch (loaderError: any) {
      if (!signal.aborted) {
        setError(loaderError?.message || 'Unable to load data.');
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    run(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return {
    data,
    loading,
    error,
    reload: async () => {
      const controller = new AbortController();
      await run(controller.signal);
    },
    setData,
  };
};
